import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { firebaseConfigValid } from '@/lib/firebase';
import { canAccessRoute } from '@/lib/roles';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle, ShieldX } from 'lucide-react';

export default function ProtectedRoute({ children }) {
  const { user, loading, role, unauthorized, disabled, signOut } = useAuth();
  const location = useLocation();

  if (!firebaseConfigValid) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8" data-testid="missing-config-screen">
        <div className="max-w-lg w-full bg-white rounded-lg border border-slate-200 shadow-sm p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-amber-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Firebase non configurato</h1>
          </div>
          <p className="text-slate-600 mb-4">
            Per utilizzare ETO Support, configura le variabili ambiente Firebase nel file <code className="bg-slate-100 px-1.5 py-0.5 rounded text-sm font-mono">.env</code>
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-md p-4 font-mono text-sm text-slate-700 space-y-1">
            <p>REACT_APP_FIREBASE_API_KEY=...</p>
            <p>REACT_APP_FIREBASE_AUTH_DOMAIN=...</p>
            <p>REACT_APP_FIREBASE_PROJECT_ID=...</p>
            <p>REACT_APP_FIREBASE_STORAGE_BUCKET=...</p>
            <p>REACT_APP_FIREBASE_MESSAGING_SENDER_ID=...</p>
            <p>REACT_APP_FIREBASE_APP_ID=...</p>
          </div>
          <p className="text-sm text-slate-500 mt-4">
            Ottieni queste credenziali dalla <a href="https://console.firebase.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Firebase Console</a> &rarr; Project Settings &rarr; Web App.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="loading-screen">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // Profilo non ancora caricato: mostra loading (evita flash della dashboard)
  if (!role && !unauthorized && !disabled) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="loading-profile-screen">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (unauthorized) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-white rounded-lg border border-slate-200 shadow-sm p-8 text-center">
          <div className="p-3 rounded-full bg-amber-50 inline-block mb-4">
            <ShieldX className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">Accesso non autorizzato</h2>
          <p className="text-sm text-slate-500 mb-4">
            Il tuo account ({user.email}) non ha un invito attivo. Contatta un amministratore HQ o Fastweb per ricevere un invito.
          </p>
          <Button onClick={() => signOut()} variant="outline">Esci</Button>
        </div>
      </div>
    );
  }

  if (disabled) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-white rounded-lg border border-slate-200 shadow-sm p-8 text-center">
          <div className="p-3 rounded-full bg-red-50 inline-block mb-4">
            <ShieldX className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">Accesso revocato</h2>
          <p className="text-sm text-slate-500 mb-4">
            L'accesso per il tuo account ({user.email}) è stato revocato. Contatta un amministratore per maggiori informazioni.
          </p>
          <Button onClick={() => signOut()} variant="outline">Esci</Button>
        </div>
      </div>
    );
  }

  if (role && !canAccessRoute(role, location.pathname)) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-8">
        <div className="max-w-md w-full bg-white rounded-lg border border-slate-200 shadow-sm p-8 text-center">
          <div className="p-3 rounded-full bg-red-50 inline-block mb-4">
            <ShieldX className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">Accesso negato</h2>
          <p className="text-sm text-slate-500">Non hai i permessi per accedere a questa pagina.</p>
        </div>
      </div>
    );
  }

  return children;
}
